import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { EIntegrationProvider } from '@simOn/common/providers';
import {
  EProviderType,
  TIntegrationTypes,
  TIotTypes,
  TProviderConnectionInterface,
  TScanTypes
} from '@simOn/provider/connection/models';

export type Providers = {
  providers: {
    iot: {
      selected: TIotTypes | undefined;
      items: TIotTypes[];
      isLoaded: boolean;
    };
    integration: {
      selected: TIntegrationTypes | undefined;
      items: TIntegrationTypes[];
      isLoaded: boolean;
    };
    scan: {
      selected: TScanTypes | undefined;
      items: TScanTypes[];
      isLoaded: boolean;
    };
  };
  isLoaded: boolean;
};

const initialState: Providers = {
  providers: {
    iot: {
      selected: undefined,
      items: [],
      isLoaded: false
    },
    integration: {
      selected: undefined,
      items: [],
      isLoaded: false
    },
    scan: {
      selected: undefined,
      items: [],
      isLoaded: false
    }
  },
  isLoaded: false
};
export const ProviderStore = signalStore(
  withState(initialState),
  withComputed(({ providers, isLoaded }) => ({
    getIotProviders: computed(() => providers.iot().items),
    /** At least one IoT hub (master device) is connected; devices and scenes need one. */
    hasIotHub: computed(() => providers.iot().items.length > 0),
    getIntegrationProviders: computed(() => providers.integration().items),
    getScanProviders: computed(() => providers.scan().items),

    iotProvidersLoaded: computed(() => providers.iot().isLoaded),
    integrationProvidersLoaded: computed(() => providers.integration().isLoaded),
    scanProvidersLoaded: computed(() => providers.scan().isLoaded),
    isMaximoConnected: computed(() =>
      providers
        .integration()
        .items.some(
          (provider) =>
            provider.providerType === EProviderType.INTEGRATION &&
            provider.integrationProvider === EIntegrationProvider.MAXIMO
        )
    ),
    getMaximoProvider: computed(() =>
      providers
        .integration()
        .items.find(
          (provider) =>
            provider.providerType === EProviderType.INTEGRATION &&
            provider.integrationProvider === EIntegrationProvider.MAXIMO
        )
    ),
    providersLoaded: computed(() => {
      const iot = providers.iot().isLoaded;
      const integration = providers.integration().isLoaded;
      const scan = providers.scan().isLoaded;
      return iot && integration && scan;
    })
  })),
  withMethods((store) => ({
    loadAllProviders(items: TProviderConnectionInterface[]): void {
      const iot = items.filter((e) => e.providerType === EProviderType.IOT) as TIotTypes[];
      this.loadIoT(iot);

      const integration = items.filter((e) => e.providerType === EProviderType.INTEGRATION) as TIntegrationTypes[];
      this.loadIntegration(integration);

      const scan = items.filter((e) => e.providerType === EProviderType.SCAN) as TScanTypes[];
      this.loadScan(scan);
    },
    loadIoT(items: TIotTypes[]): void {
      patchState(store, ({ providers }) => ({
        providers: {
          ...providers,
          iot: {
            ...providers.iot,
            items,
            isLoaded: true
          }
        }
      }));
    },
    loadIntegration(items: TIntegrationTypes[]): void {
      patchState(store, ({ providers }) => ({
        providers: {
          ...providers,
          integration: {
            ...providers.integration,
            items,
            isLoaded: true
          }
        }
      }));
    },
    loadScan(items: TScanTypes[]): void {
      patchState(store, ({ providers }) => ({
        providers: {
          ...providers,
          scan: {
            ...providers.scan,
            items,
            isLoaded: true
          }
        }
      }));
    },
    removeIot(item: TIotTypes): void {
      patchState(store, ({ providers }) => {
        const providersExcludingDeleted = providers.iot.items.filter(
          (ele) => ele.masterDeviceId === item.masterDeviceId
        );
        return {
          providers: {
            ...providers,
            iot: {
              ...providers.iot,
              items: providersExcludingDeleted
            }
          }
        };
      });
    },
    removeIntegration(item: TIntegrationTypes): void {
      patchState(store, ({ providers }) => {
        const providersExcludingDeleted = providers.integration.items.filter(
          (ele) => ele.orgId === item.orgId && ele.siteId === item.siteId
        );
        return {
          providers: {
            ...providers,
            integration: {
              ...providers.integration,
              items: providersExcludingDeleted
            }
          }
        };
      });
    },
    removeScan(item: TScanTypes): void {
      patchState(store, ({ providers }) => {
        const providersExcludingDeleted = providers.scan.items.filter((ele) => ele.scanProvider === item.scanProvider);
        return {
          providers: {
            ...providers,
            scan: {
              ...providers.scan,
              items: providersExcludingDeleted
            }
          }
        };
      });
    },
    changeMaximoServerUrl(serverUrl: string): void {
      patchState(store, ({ providers }) => {
        return {
          providers: {
            ...providers,
            integration: {
              ...providers.integration,
              items: [
                ...providers.integration.items.map((item) =>
                  item.integrationProvider === EIntegrationProvider.MAXIMO ? { ...item, serverUrl } : item
                )
              ]
            }
          }
        };
      });
    }
  }))
);
